import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ScoreRing from './ScoreRing';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCopy } from '../context/CopyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/career-growth`;

const HUES = {
  Academic: '#60a5fa', Skills: '#22d3ee', Experience: '#34d399',
  'Professional Presence': '#a78bfa', Network: '#f472b6', 'Job Search': '#fbbf24',
};

export const ReadinessMini = () => {
  const { getAuthHeader } = useAuth();
  const { nav } = useCopy();
  const title = nav['/app/readiness'] || 'Career Launch Readiness';
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/readiness`, getAuthHeader());
      setD(res.data);
    } catch { /* readiness is additive, never block the dashboard */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (!d) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl p-5"
      style={{ background: 'linear-gradient(160deg, rgba(8,26,58,0.85), rgba(6,18,42,0.78))', border: '1px solid rgba(57,227,220,0.22)', boxShadow: '0 0 1px rgba(57,227,220,0.4), 0 0 24px -6px rgba(57,227,220,0.18), 0 0 50px -10px rgba(57,227,220,0.06)' }}
      data-testid="dashboard-readiness">
      <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, #39E3DC, transparent)', boxShadow: '0 0 10px rgba(57,227,220,0.4)' }} />
      <span className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #39E3DC, transparent 65%)' }} />
      <div className="relative flex flex-wrap items-center gap-6">
        <ScoreRing value={d.overall} label={d.band} palette="cyan" size={130} testId="dashboard-readiness-score" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300">{title}</p>
          <p className="text-sm text-slate-300 mt-1">
            {d.target ? <>Measured against <span className="text-white font-medium">{d.target}</span></> : 'No target career set yet'}
          </p>
          <div className="mt-3 space-y-1.5">
            {(d.components || []).map(c => (
              <div key={c.key} className="flex items-center gap-2">
                <span className="w-40 text-[11px] text-slate-300 truncate">{c.label}</span>
                <div className="fp-meter flex-1" style={{ height: 6 }}>
                  <div className="fp-meter-fill" style={{
                    width: `${c.score}%`, background: HUES[c.label] || '#22d3ee',
                    boxShadow: `0 0 10px ${HUES[c.label] || '#22d3ee'}99`,
                  }} />
                </div>
                <span className="w-7 text-right text-[11px] fp-num" style={{ color: HUES[c.label] }}>{c.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="min-w-[200px]">
          {(d.top_actions || []).slice(0, 1).map((a, i) => (
            <div key={i}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Do this next</p>
              <p className="text-sm text-white mt-1">{a.action}</p>
              <p className="text-xs text-slate-300 mt-1">{a.why}</p>
              <Link to={a.path} className="mt-3 inline-flex items-center gap-1 text-xs text-cyan-300 hover:gap-2 transition-all duration-200"
                data-testid="dashboard-readiness-action">
                Go there <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
          <Link to="/app/readiness" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition-colors duration-200"
            data-testid="dashboard-readiness-link">
            Open {title} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReadinessMini;
